import { Link } from 'react-router';

import type { ServiceKey } from '@shared/api/config';
import { cn } from '@shared/ui/cn';

import { copy } from './copy';
import { settingsPathFor, type ServiceSectionSpec } from './services';

/**
 * The strip that carries every service the operator administers, from inside
 * any one of them.
 *
 * **It is what makes the split navigable rather than merely shorter.** Without
 * it, moving from the auth table to the content table means going back to the
 * index and forward again — two navigations for a move between siblings — and
 * the split would have traded one long page for a slower one.
 *
 * The active mark is the **block** edge, not the inline edge. The navigation
 * column marks its current entry with a rule at `border-inline-start`; in a
 * horizontal strip that same rule reads as a divider *between* two entries
 * rather than a mark *on* one. This is the shape `PagesStrip` already uses for
 * the two informational pages, so the console has one horizontal-strip anatomy
 * rather than two. Colour is never alone: `aria-current="page"`, the accent
 * fill, and the weight all hold.
 */
export function SettingsStrip({
  sections,
  current,
}: {
  readonly sections: readonly ServiceSectionSpec[];
  /** Absent on the index, where no service is open. */
  readonly current?: ServiceKey;
}) {
  if (sections.length === 0) return null;

  return (
    <nav aria-label={copy.strip.label} className="mb-6 flex flex-wrap gap-2 border-b border-border">
      {sections.map((spec) => {
        const active = spec.key === current;

        return (
          <Link
            key={spec.key}
            to={settingsPathFor(spec.key)}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'hit-target -mb-px flex items-center gap-3 whitespace-nowrap rounded-t-sm border-b-2 px-3 py-2 text-sm no-underline',
              active
                ? 'border-b-accent bg-accent-soft font-semibold text-accent-text'
                : 'border-b-transparent text-ink-muted hover:bg-hover-bg hover:text-ink',
            )}
          >
            <span>{spec.heading}</span>
          </Link>
        );
      })}
    </nav>
  );
}
